import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

interface RegisterResult {
  email: string;
  role: string;
  pendingApproval?: boolean;
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const defaultEmail = useMemo(() => searchParams.get("email") ?? "", [searchParams]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // F-012 (2026-05-11): the CP `/api/auth/register` handler requires
  // `acceptedTerms: true` (services/control-plane/src/auth/routes.ts:81),
  // and rejects with HTTP 400 "Terms acknowledgment required" otherwise.
  // Before this fix, the form omitted the field entirely — registration
  // was completely broken from the UI.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<RegisterResult | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!acceptedTerms) {
      setError("Please acknowledge the terms to continue");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: name.trim() || email,
          password,
          // BugBot 2026-05-11 (PR #549 MEDIUM): use the state variable
          // so consent is genuinely coupled to user action, not a
          // hardcoded literal. The guard above already aborts the
          // submission when acceptedTerms is false.
          acceptedTerms,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as RegisterResult & { error?: string };
      if (!res.ok) {
        throw new Error(data.error ?? "Unable to create account");
      }
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create account");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--c-bg)",
        padding: "1rem",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          borderRadius: 12,
          padding: "1.5rem",
        }}
      >
        <div style={{ marginBottom: "1rem", textAlign: "center" }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--c-text-faint)",
              marginBottom: 8,
            }}
          >
            AgentMe
          </div>
          <h1 style={{ margin: 0, fontSize: "2rem", fontWeight: 600, color: "var(--c-text)" }}>
            Create Account
          </h1>
          <p style={{ margin: "0.25rem 0 0", color: "var(--c-text-secondary)" }}>
            Use this once after waitlist approval.
          </p>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit}>
            <label
              htmlFor="register-name"
              style={{
                display: "block",
                fontSize: 12,
                marginBottom: 6,
                color: "var(--c-text-secondary)",
              }}
            >
              Your name
            </label>
            <input
              id="register-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Account owner name"
              autoComplete="name"
              style={{
                width: "100%",
                marginBottom: 12,
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                background: "var(--c-surface-2)",
                color: "var(--c-text)",
                padding: "0.6rem 0.7rem",
              }}
            />

            <label
              htmlFor="register-email"
              style={{
                display: "block",
                fontSize: 12,
                marginBottom: 6,
                color: "var(--c-text-secondary)",
              }}
            >
              Email
            </label>
            <input
              id="register-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              style={{
                width: "100%",
                marginBottom: 12,
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                background: "var(--c-surface-2)",
                color: "var(--c-text)",
                padding: "0.6rem 0.7rem",
              }}
            />

            <label
              htmlFor="register-password"
              style={{
                display: "block",
                fontSize: 12,
                marginBottom: 6,
                color: "var(--c-text-secondary)",
              }}
            >
              Password
            </label>
            <input
              id="register-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create password"
              autoComplete="new-password"
              type="password"
              required
              style={{
                width: "100%",
                marginBottom: 12,
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                background: "var(--c-surface-2)",
                color: "var(--c-text)",
                padding: "0.6rem 0.7rem",
              }}
            />

            <label
              htmlFor="register-confirm-password"
              style={{
                display: "block",
                fontSize: 12,
                marginBottom: 6,
                color: "var(--c-text-secondary)",
              }}
            >
              Confirm password
            </label>
            <input
              id="register-confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              autoComplete="new-password"
              type="password"
              required
              style={{
                width: "100%",
                marginBottom: 12,
                borderRadius: 8,
                border: "1px solid var(--c-border)",
                background: "var(--c-surface-2)",
                color: "var(--c-text)",
                padding: "0.6rem 0.7rem",
              }}
            />

            <label
              htmlFor="register-accept-terms"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 13,
                marginBottom: 12,
                color: "var(--c-text-secondary)",
                cursor: "pointer",
              }}
            >
              <input
                id="register-accept-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I acknowledge the{" "}
                <a
                  href="https://agentme.io/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--c-accent)" }}
                >
                  terms of service
                </a>{" "}
                and{" "}
                <a
                  href="https://agentme.io/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--c-accent)" }}
                >
                  privacy policy
                </a>
                .
              </span>
            </label>

            {error ? (
              <p style={{ color: "var(--c-danger)", marginBottom: 12 }} role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={loading || !email || !password || !confirmPassword || !acceptedTerms}
              style={{
                width: "100%",
                borderRadius: 8,
                border: "none",
                padding: "0.65rem 0.75rem",
                background: "var(--c-accent)",
                color: "var(--c-accent-foreground)",
                fontWeight: 600,
              }}
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
        ) : (
          <div>
            <p style={{ color: "var(--c-success)", marginBottom: 12 }}>
              {result.pendingApproval
                ? "Account created, but waitlist approval is still pending."
                : "Account created. Sign in to continue."}
            </p>
            <Link to="/login" style={{ color: "var(--c-accent)", textDecoration: "underline" }}>
              Go to sign in
            </Link>
          </div>
        )}

        <p style={{ marginTop: "1rem", fontSize: 12, color: "var(--c-text-faint)" }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
